// OPTIONS ANALYSIS · Validation — every ITM CE × every ITM PE for one underlying, both modes,
// with the raw inputs and the qualify/reject reason, for manual comparison against Kite.
import React, { useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Download } from "lucide-react";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { age, EconomicsNote, LegStatus, MarketContext, px, qty, ScannerStatusBar, UnderlyingPicker, usePoll } from "@/components/options/OptionsCommon";
import { ContractDepthDrawer } from "@/components/options/DepthDrawer";

function LegTable({ title, legs, testId }) {
  return (
    <div className="term-panel rounded-sm overflow-auto">
      <div className="px-2 py-1 text-xs font-bold bg-[var(--term-s3)]">{title} ({legs.length})</div>
      <table className="w-full text-[11px] border-collapse" data-testid={testId}>
        <thead className="text-[var(--term-muted)]"><tr>
          {["ITM#", "Symbol", "Strike", "LTP", "Bid", "Bid Qty", "Offer", "Offer Qty", "OI", "Volume", "Last trade", "Trade age", "Tick age", "LTP mode", "OFFER mode"]
            .map((h) => <th key={h} className="px-1.5 py-1 text-right whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {legs.map((l) => (
            <tr key={l.instrument_token} className="border-t border-[var(--term-border)] mono">
              <td className="px-1.5 py-0.5 text-right">{l.itm_rank}</td>
              <td className="px-1.5 py-0.5 text-right">{l.tradingsymbol}</td>
              <td className="px-1.5 py-0.5 text-right font-bold">{l.strike}</td>
              <td className="px-1.5 py-0.5 text-right">{px(l.ltp)}</td>
              <td className="px-1.5 py-0.5 text-right text-[#2563EB]">{px(l.bid)}</td>
              <td className="px-1.5 py-0.5 text-right">{qty(l.bid_qty)}</td>
              <td className="px-1.5 py-0.5 text-right text-[#FF3B30]">{px(l.ask)}</td>
              <td className="px-1.5 py-0.5 text-right">{qty(l.ask_qty)}</td>
              <td className="px-1.5 py-0.5 text-right">{qty(l.oi)}</td>
              <td className="px-1.5 py-0.5 text-right">{qty(l.volume)}</td>
              <td className="px-1.5 py-0.5 text-right">{l.last_trade_time ? new Date(l.last_trade_time).toLocaleTimeString() : "—"}</td>
              <td className="px-1.5 py-0.5 text-right">{age(l.trade_age_s)}</td>
              <td className="px-1.5 py-0.5 text-right">{age(l.tick_age_s)}</td>
              <td className="px-1.5 py-0.5 text-right"><LegStatus value={l.ltp_status} /></td>
              <td className="px-1.5 py-0.5 text-right"><LegStatus value={l.offer_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Result({ r }) {
  if (r.near) return <span className="text-[10px] font-bold text-[var(--term-muted)]" title="analysis only - never executable">NEAR · {r.reason}</span>;
  const ok = r.reason === "QUALIFIED";
  const warned = r.reason === "QUALIFIED_WITH_WARNINGS";
  const w = (r.warnings || []).join(", ");
  return <span className={`text-[10px] font-bold ${ok ? "text-[#00C805]" : warned ? "text-[#B45309]" : "text-[var(--term-muted)]"}`} title={w}>
    {r.reason}{w && <span className="font-normal"> · {w}</span>}</span>;
}

export default function OptionsValidation() {
  const status = usePoll("/status", { ms: 5000 });
  const unds = useMemo(() => status.data?.runtime?.scanner?.underlyings || [], [status.data]);
  const [und, setUnd] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [only, setOnly] = useState("ALL");
  const [depth, setDepth] = useState(null);
  useEffect(() => { if (!und && unds.length) setUnd(unds[0].underlying); }, [unds, und]);
  const capture = () => optionsApi.get("/validation", { params: { underlying: und } })
    .then((r) => { setD(r.data); setErr(null); }).catch((e) => setErr(optionsError(e)));
  useEffect(() => { if (und) capture(); }, [und]); // eslint-disable-line react-hooks/exhaustive-deps

  const pairs = useMemo(() => (d?.pairs || []).filter((p) =>
    only === "ALL" || (only === "LTP" && p.LTP.qualified) || (only === "OFFER" && p.OFFER.qualified)), [d, only]);

  const download = () => optionsApi.get("/validation.csv", { params: { underlying: und }, responseType: "blob" })
    .then((r) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `options_validation_${und}_${d?.expiry || ""}.csv`;
      a.click();
    }).catch((e) => setErr(optionsError(e)));

  return (
    <div className="p-4 space-y-3" data-testid="options-validation-page">
      <h1 className="text-lg font-bold flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Options Analysis · Validation</h1>
      <ScannerStatusBar status={status.data} onChanged={status.reload} />
      <MarketContext market={d?.market || status.data?.settings?.market} universeLabel={status.data?.runtime?.scanner?.universe_label}
        feedState={status.data?.runtime?.feed_state} />
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <UnderlyingPicker underlyings={unds} value={und} onChange={setUnd} testId="options-validation-underlying" />
        <button onClick={capture} className="px-2 py-1 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="options-validation-capture">Capture now</button>
        <button onClick={download} className="px-2 py-1 rounded-sm font-bold bg-[var(--term-s3)] border border-[var(--term-border-strong)] flex items-center gap-1"><Download className="h-3 w-3" /> CSV</button>
        <select value={only} onChange={(e) => setOnly(e.target.value)} className="bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1">
          <option value="ALL">All pairs</option><option value="LTP">Qualified in LTP</option><option value="OFFER">Qualified in OFFER</option>
        </select>
        {err && <span className="text-[#FF3B30]">{err}</span>}
      </div>
      {d && (
        <>
          <div className="term-panel rounded-sm p-2 text-xs mono flex flex-wrap gap-x-4 gap-y-1" data-testid="options-validation-summary">
            <span>{d.underlying} · {d.underlying_symbol} <b>{px(d.spot)}</b> ({age(d.spot_age_s)}) {d.spot_problem || ""}</span>
            <span>expiry {d.expiry}</span>
            <EconomicsNote e={d.economics} />
            <span>captured {new Date(d.generated_at).toLocaleTimeString()}</span>
            <span>pairs {d.summary?.pairs ?? 0} · qualified LTP {d.summary?.qualified?.LTP ?? 0} · OFFER {d.summary?.qualified?.OFFER ?? 0}
              {" "}· best (valid legs) LTP <b>{px(d.summary?.best_opp?.LTP)}</b> / OFFER <b>{px(d.summary?.best_opp?.OFFER)}</b></span>
            <span className="text-[var(--term-muted)]">min OI {d.settings.min_oi} · max ITM {d.settings.max_itm_calls}/{d.settings.max_itm_puts} · trade age ≤{d.settings.max_trade_age_s}s · tick ≤{d.settings.max_tick_age_s}s · depth ≤{d.settings.max_depth_age_s}s · volume today {String(d.settings.require_volume_today)} · ATM {d.settings.atm_rule}</span>
            {d.summary && <span className="w-full text-[var(--term-muted)]">LTP reasons {JSON.stringify(d.summary.reasons.LTP)} · OFFER reasons {JSON.stringify(d.summary.reasons.OFFER)}</span>}
          </div>
          <div className="grid xl:grid-cols-2 gap-3">
            <LegTable title="Selected ITM CALLS (strike < underlying)" legs={d.calls} testId="options-validation-calls" />
            <LegTable title="Selected ITM PUTS (strike > underlying)" legs={d.puts} testId="options-validation-puts" />
          </div>
          <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "60vh" }}>
            <table className="w-full text-[11px] border-collapse" data-testid="options-validation-pairs">
              <thead className="sticky top-0 bg-[var(--term-s3)] text-[var(--term-secondary)]"><tr>
                {["CE#", "PE#", "CE", "PE", "Strike diff", "CE LTP", "PE LTP", "LTP combined", "LTP opp", "LTP result",
                  "CE Offer", "PE Offer", "Offer combined", "Offer opp", "OFFER result", "CE OI", "PE OI", "Lot (qty)", "LTP opp/lot", "Offer opp/lot", "LTP entry/lot", "Offer entry/lot"]
                  .map((h) => <th key={h} className="px-1.5 py-1 text-right whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {pairs.map((p) => (
                  <tr key={p.pair_id} className="border-t border-[var(--term-border)] mono cursor-pointer hover:bg-[var(--term-hover)]"
                    title="Click for both legs' market depth"
                    onClick={() => {
                      const ce = d.calls.find((c) => c.strike === p.ce_strike);
                      const pe = d.puts.find((x) => x.strike === p.pe_strike);
                      setDepth({ heading: `${d.underlying} ${p.ce_strike} CE / ${p.pe_strike} PE · ${d.expiry}`,
                        legs: [{ token: ce.instrument_token, title: "CALL (CE)", leg: ce }, { token: pe.instrument_token, title: "PUT (PE)", leg: pe }] });
                    }}>
                    <td className="px-1.5 py-0.5 text-right">{p.ce_itm_rank}</td>
                    <td className="px-1.5 py-0.5 text-right">{p.pe_itm_rank}</td>
                    <td className="px-1.5 py-0.5 text-right">{p.ce_strike}</td>
                    <td className="px-1.5 py-0.5 text-right">{p.pe_strike}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.strike_diff)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.ce_ltp)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.pe_ltp)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.LTP.combined)}</td>
                    <td className={`px-1.5 py-0.5 text-right ${p.LTP.opp > 0 ? "text-[#00C805]" : ""}`}>{px(p.LTP.opp)}</td>
                    <td className="px-1.5 py-0.5 text-right"><Result r={p.LTP} /></td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.ce_offer)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.pe_offer)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.OFFER.combined)}</td>
                    <td className={`px-1.5 py-0.5 text-right ${p.OFFER.opp > 0 ? "text-[#00C805]" : ""}`}>{px(p.OFFER.opp)}</td>
                    <td className="px-1.5 py-0.5 text-right"><Result r={p.OFFER} /></td>
                    <td className="px-1.5 py-0.5 text-right">{qty(p.ce_oi)}</td>
                    <td className="px-1.5 py-0.5 text-right">{qty(p.pe_oi)}</td>
                    <td className="px-1.5 py-0.5 text-right">{p.lot_size ?? "—"}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.LTP.opp_lot)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.OFFER.opp_lot)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.LTP.entry_value_lot)}</td>
                    <td className="px-1.5 py-0.5 text-right">{px(p.OFFER.entry_value_lot)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-[var(--term-muted)]">
            A snapshot captured on demand from the live feed records the scanner uses. Opportunity = (PE strike − CE strike) −
            combined entry price; shown for every pair even when a leg is invalid, but a pair QUALIFIES in a mode only when both legs are
            valid in that mode and the opportunity is &gt; 0. Click a pair for both legs' live market depth.
          </div>
        </>
      )}
      {depth && <ContractDepthDrawer legs={depth.legs} heading={depth.heading} onClose={() => setDepth(null)} />}
    </div>
  );
}
